import React from "react";
import Icon from "../../../components/AppIcon";
import Waveform from "./Waveform";
import JogWheel from "./JogWheel";

const Deck = ({
  id,
  deckState,
  playDeck,
  pauseDeck,
  seekDeck,
  setDeckPitch,
  nudgeDeckPitch,
  syncDeckBpm,
  setDeckLoop,
  setDeckCrossfadeSide,
  triggerCue,
  setCuePoint,
  clearCuePoint,
  setDeckDelay,
  setDeckReverb,
  setDeckDistortion,
  setDeckMute,
  setDeckSolo,
  scratchDeck,
  releaseScratch,
  otherDeckBpm,
  color = "primary",
}) => {
  const isLoaded = deckState && deckState.duration > 0;

  // Formatting seconds into MM:SS
  const formatTime = (secs) => {
    if (isNaN(secs)) return "00:00";
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const currentPlayTime = deckState ? deckState.currentTime : 0;
  const totalDuration = deckState ? deckState.duration : 0;
  const timeRemaining = Math.max(0, totalDuration - currentPlayTime);

  // FX values
  const fxConfig = deckState
    ? deckState.fxConfig
    : {
        delayMix: 0,
        delayTime: 0.3,
        delayFeedback: 0.4,
        reverbMix: 0,
        distortionAmount: 0,
      };

  const colorStyles = {
    primary: {
      text: "text-primary",
      border: "border-primary/30",
      bg: "bg-primary/5",
      buttonActive: "bg-primary text-black shadow-terminal-glow",
      buttonBorder: "border-primary/20 text-primary hover:bg-primary/10",
      deckGlow: "shadow-[0_0_25px_rgba(0,255,65,0.06)]",
    },
    cyan: {
      text: "text-terminal-cyan",
      border: "border-terminal-cyan/30",
      bg: "bg-terminal-cyan/5",
      buttonActive:
        "bg-terminal-cyan text-black shadow-[0_0_15px_rgba(0,255,170,0.4)]",
      buttonBorder:
        "border-terminal-cyan/20 text-terminal-cyan hover:bg-terminal-cyan/10",
      deckGlow: "shadow-[0_0_25px_rgba(0,255,170,0.06)]",
    },
    blue: {
      text: "text-terminal-blue",
      border: "border-terminal-blue/30",
      bg: "bg-terminal-blue/5",
      buttonActive:
        "bg-terminal-blue text-black shadow-[0_0_15px_rgba(74,158,255,0.4)]",
      buttonBorder:
        "border-terminal-blue/20 text-terminal-blue hover:bg-terminal-blue/10",
      deckGlow: "shadow-[0_0_25px_rgba(74,158,255,0.06)]",
    },
    warning: {
      text: "text-warning",
      border: "border-warning/30",
      bg: "bg-warning/5",
      buttonActive:
        "bg-warning text-black shadow-[0_0_15px_rgba(255,165,0,0.4)]",
      buttonBorder: "border-warning/20 text-warning hover:bg-warning/10",
      deckGlow: "shadow-[0_0_25px_rgba(255,165,0,0.06)]",
    },
  }[color];

  // Sync BPM
  const handleSync = () => {
    if (otherDeckBpm && syncDeckBpm) {
      syncDeckBpm(id, otherDeckBpm);
    }
  };

  // Loop beat divisions
  const triggerLoopBeats = (beatsCount) => {
    if (!isLoaded) return;

    // Check if loop is currently active
    const isCurrentlyLooping = deckState.loop;

    if (isCurrentlyLooping) {
      // Turn off loop
      setDeckLoop(id, false);
    } else {
      // Start loop from current time, spanning beatsCount beats
      // Calculate beat length in seconds: beatDuration = 60 / BPM
      const bpm = deckState.bpm || 120;
      const beatSec = 60 / bpm;
      const loopLen = beatSec * beatsCount;
      const start = currentPlayTime;
      const end = Math.min(totalDuration, start + loopLen);

      setDeckLoop(id, true, start, end);
    }
  };

  return (
    <div
      className={`bg-card border border-border rounded-lg p-4 flex flex-col justify-between h-full relative ${colorStyles.deckGlow}`}
    >
      {/* Deck Indicator Header */}
      <div className="flex justify-between items-center border-b border-border/40 pb-2 mb-3">
        <div className="flex items-center space-x-2">
          <div
            className={`w-2.5 h-2.5 rounded-full ${deckState?.isPlaying ? "bg-success animate-pulse" : "bg-zinc-600"}`}
          />
          <span
            className={`font-fira font-bold uppercase tracking-wider text-sm ${colorStyles.text}`}
          >
            DECK {id}
          </span>
        </div>
        {/* Track Title Info */}
        <div className="flex-grow mx-4 overflow-hidden text-center">
          <div className="text-xs font-bold text-foreground truncate uppercase font-source">
            {deckState?.name || "EMPTY DECK"}
          </div>
          {isLoaded && deckState.channel && (
            <div className="text-[9px] text-muted-foreground truncate uppercase leading-none font-source">
              Artist: {deckState.channel}
            </div>
          )}
        </div>
        {/* Route Channel Side Selector */}
        <div className="flex items-center space-x-1 bg-zinc-950 border border-zinc-800 rounded p-0.5 text-[9px] font-fira">
          <span className="text-[8px] text-muted-foreground mr-1 uppercase">
            Route
          </span>
          {["left", "center", "right"].map((side) => (
            <button
              key={side}
              onClick={() => setDeckCrossfadeSide(id, side)}
              className={`px-1.5 py-0.5 rounded uppercase font-bold transition text-[8px] ${
                deckState?.crossfadeSide === side
                  ? colorStyles.buttonActive
                  : "text-muted-foreground hover:text-foreground hover:bg-zinc-800"
              }`}
            >
              {side === "left" ? "L" : side === "right" ? "R" : "M"}
            </button>
          ))}
        </div>
      </div>

      {/* Waveform View */}
      <div className="mb-4">
        <Waveform
          id={id}
          audioBuffer={deckState?.audioBuffer}
          currentTime={currentPlayTime}
          duration={totalDuration}
          loopActive={deckState?.loop}
          loopStart={deckState?.loopStart}
          loopEnd={deckState?.loopEnd}
          cues={deckState?.cues}
          onSeek={(time) => seekDeck(id, time)}
          color={color}
        />
      </div>

      {/* Platter, Pitch, and EQ/FX Grid */}
      <div className="grid grid-cols-12 gap-3 flex-grow my-2">
        {/* FX Rack Module (Col: 3) */}
        <div className="col-span-3 border-r border-zinc-800/40 pr-2 flex flex-col justify-between font-source text-[9px]">
          <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider mb-2 text-center border-b border-zinc-900 pb-1">
            FX Rack
          </span>
          {/* Echo Delay */}
          <div className="flex flex-col space-y-1 mb-2">
            <div className="flex justify-between items-center text-muted-foreground">
              <span>DELAY</span>
              <span className={colorStyles.text}>
                {Math.round(fxConfig.delayMix * 100)}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="0.8"
              step="0.05"
              value={fxConfig.delayMix}
              onChange={(e) =>
                setDeckDelay(
                  id,
                  parseFloat(e.target.value),
                  fxConfig.delayTime,
                  fxConfig.delayFeedback,
                )
              }
              disabled={!isLoaded}
              className="w-full h-1 cursor-pointer accent-primary bg-zinc-800"
            />
          </div>
          {/* Reverb */}
          <div className="flex flex-col space-y-1 mb-2">
            <div className="flex justify-between items-center text-muted-foreground">
              <span>REVERB</span>
              <span className={colorStyles.text}>
                {Math.round(fxConfig.reverbMix * 100)}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="0.8"
              step="0.05"
              value={fxConfig.reverbMix}
              onChange={(e) => setDeckReverb(id, parseFloat(e.target.value))}
              disabled={!isLoaded}
              className="w-full h-1 cursor-pointer accent-primary bg-zinc-800"
            />
          </div>
          {/* Bit Crusher / Distortion */}
          <div className="flex flex-col space-y-1 mb-2">
            <div className="flex justify-between items-center text-muted-foreground">
              <span>CRUSH</span>
              <span className={colorStyles.text}>
                {Math.round(fxConfig.distortionAmount * 100)}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="0.8"
              step="0.05"
              value={fxConfig.distortionAmount}
              onChange={(e) =>
                setDeckDistortion(id, parseFloat(e.target.value))
              }
              disabled={!isLoaded}
              className="w-full h-1 cursor-pointer accent-primary bg-zinc-800"
            />
          </div>

          {/* Mute & Solo channel strips */}
          <div className="grid grid-cols-2 gap-1.5 mt-auto pt-2 border-t border-zinc-900">
            <button
              onClick={() => setDeckMute(id, !deckState.isMuted)}
              disabled={!isLoaded}
              className={`py-1 rounded text-[9px] font-bold border transition ${
                deckState?.isMuted
                  ? "bg-destructive border-destructive text-white"
                  : "border-zinc-800 text-zinc-400 hover:text-foreground hover:bg-zinc-900"
              }`}
            >
              MUTE
            </button>
            <button
              onClick={() => setDeckSolo(id, !deckState.isSoloed)}
              disabled={!isLoaded}
              className={`py-1 rounded text-[9px] font-bold border transition ${
                deckState?.isSoloed
                  ? "bg-[#d29922] border-[#d29922] text-black"
                  : "border-zinc-800 text-zinc-400 hover:text-foreground hover:bg-zinc-900"
              }`}
            >
              SOLO
            </button>
          </div>
        </div>

        {/* Jog Wheel Vinyl Platter (Col: 6) */}
        <div className="col-span-6 flex flex-col items-center justify-center">
          <JogWheel
            id={id}
            isPlaying={deckState?.isPlaying ?? false}
            bpm={deckState?.bpm ?? 120}
            playbackRate={deckState?.playbackRate ?? 1.0}
            currentTime={currentPlayTime}
            duration={totalDuration}
            onScratch={(factor) => scratchDeck(id, factor)}
            onReleaseScratch={() => releaseScratch(id)}
            color={color}
          />
        </div>

        {/* Pitch Slider / CDJ Speed Control (Col: 3) */}
        <div className="col-span-3 border-l border-zinc-800/40 pl-2 flex flex-col justify-between items-center font-source">
          <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider mb-2 text-center border-b border-zinc-900 pb-1 w-full">
            Pitch
          </span>

          {/* Sync Button */}
          <button
            onClick={handleSync}
            disabled={!isLoaded || !otherDeckBpm}
            className={`w-full py-1 text-[10px] font-bold rounded uppercase tracking-wider border mb-4 transition ${
              Math.abs((deckState?.bpm ?? 120) - (otherDeckBpm ?? 120)) < 0.2 &&
              isLoaded
                ? colorStyles.buttonActive + " border-transparent"
                : colorStyles.buttonBorder
            }`}
          >
            SYNC BPM
          </button>

          {/* Nudge Speed Buttons */}
          <div className="flex justify-between w-full space-x-1.5 mb-2">
            <button
              onClick={() => nudgeDeckPitch(id, -1)}
              disabled={!isLoaded}
              className={`flex-grow py-0.5 rounded text-center text-[10px] font-bold border ${colorStyles.buttonBorder}`}
            >
              NUDGE -
            </button>
            <button
              onClick={() => nudgeDeckPitch(id, 1)}
              disabled={!isLoaded}
              className={`flex-grow py-0.5 rounded text-center text-[10px] font-bold border ${colorStyles.buttonBorder}`}
            >
              NUDGE +
            </button>
          </div>

          {/* Pitch Range Slider */}
          <div className="relative h-28 w-6 bg-zinc-950 border border-zinc-800 rounded flex justify-center py-2 shadow-inner">
            <input
              type="range"
              orient="vertical"
              min="-10.0"
              max="10.0"
              step="0.1"
              value={deckState?.pitchPercent ?? 0}
              onChange={(e) => setDeckPitch(id, parseFloat(e.target.value))}
              disabled={!isLoaded}
              className="w-1.5 h-full cursor-pointer accent-primary"
              style={{ WebkitAppearance: "slider-vertical" }}
            />
          </div>

          <span className="text-[10px] font-fira text-foreground font-bold mt-2">
            {deckState
              ? `${deckState.pitchPercent >= 0 ? "+" : ""}${deckState.pitchPercent.toFixed(1)}%`
              : "0.0%"}
          </span>
        </div>
      </div>

      {/* Hot Cues & Playback Button Strip */}
      <div className="border-t border-border/40 pt-3 mt-3 flex flex-col space-y-2.5">
        {/* Hot Cue Points */}
        <div className="flex items-center space-x-2">
          <span className="text-[8px] font-source text-muted-foreground uppercase">
            Hot Cues
          </span>
          <div className="flex-grow grid grid-cols-4 gap-1.5">
            {[0, 1, 2, 3].map((idx) => {
              const hasCue = deckState?.cues?.[idx] !== undefined;
              return (
                <div key={idx} className="relative group">
                  <button
                    onClick={() =>
                      hasCue
                        ? seekDeck(id, deckState.cues[idx])
                        : setCuePoint(id, idx)
                    }
                    disabled={!isLoaded}
                    className={`w-full py-1 text-[9px] font-fira font-bold rounded border transition ${
                      hasCue
                        ? "bg-[#eab308] border-[#eab308] text-black hover:bg-[#eab308]/80 shadow-[0_0_8px_rgba(234,179,8,0.3)]"
                        : "border-zinc-800 text-zinc-500 hover:text-foreground hover:bg-zinc-900"
                    }`}
                  >
                    CUE {idx + 1}
                  </button>
                  {hasCue && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearCuePoint(id, idx);
                      }}
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-600 border border-zinc-950 flex items-center justify-center text-[7px] text-white opacity-0 group-hover:opacity-100 transition shadow"
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Loop Divisions Pad */}
        <div className="flex items-center space-x-2">
          <span className="text-[8px] font-source text-muted-foreground uppercase">
            Autoloop
          </span>
          <div className="flex-grow grid grid-cols-4 gap-1.5 text-[9px] font-fira font-bold">
            {[1, 2, 4, 8].map((beats) => (
              <button
                key={beats}
                onClick={() => triggerLoopBeats(beats)}
                disabled={!isLoaded}
                className={`py-1 rounded border transition ${
                  deckState?.loop &&
                  Math.round(
                    (deckState.loopEnd - deckState.loopStart) /
                      (60 / deckState.bpm),
                  ) === beats
                    ? "bg-[#10b981] border-[#10b981] text-black shadow-[0_0_8px_rgba(16,185,129,0.3)]"
                    : "border-zinc-800 text-zinc-400 hover:text-foreground hover:bg-zinc-900"
                }`}
              >
                {beats} {beats === 1 ? "Beat" : "Beats"}
              </button>
            ))}
          </div>
        </div>

        {/* Playback Control Bar */}
        <div className="flex justify-between items-center pt-2">
          {/* Time Display */}
          <div className="font-fira bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1 text-center min-w-[100px] shadow-inner select-none">
            <div
              id={`playhead-${id}`}
              className="text-xs font-bold text-success tracking-wide leading-none"
            >
              {formatTime(currentPlayTime)}
            </div>
            <div className="text-[8px] text-muted-foreground uppercase mt-1 leading-none">
              Rem: {formatTime(timeRemaining)}
            </div>
          </div>

          {/* Trigger Play/Pause/Cue buttons */}
          <div className="flex space-x-2.5">
            {/* Cue Button (returns to active cue, plays when held) */}
            <button
              onClick={() => triggerCue(id)}
              disabled={!isLoaded}
              className="px-4 py-2 border border-yellow-500/20 text-yellow-500 rounded font-bold uppercase tracking-wider text-xs hover:bg-yellow-500/10 active:scale-95 transition"
            >
              CUE
            </button>

            {/* Play/Pause Button */}
            {deckState?.isPlaying ? (
              <button
                onClick={() => pauseDeck(id)}
                disabled={!isLoaded}
                className="px-5 py-2 bg-[#ff2e63] text-white rounded font-bold uppercase tracking-wider text-xs hover:brightness-110 active:scale-95 transition shadow-[0_4px_12px_rgba(255,46,99,0.35)]"
              >
                <div className="flex items-center space-x-1.5">
                  <Icon name="Pause" size={12} fill="currentColor" />
                  <span>PAUSE</span>
                </div>
              </button>
            ) : (
              <button
                onClick={() => playDeck(id)}
                disabled={!isLoaded}
                className="px-5 py-2 bg-[#00ff41] text-black rounded font-bold uppercase tracking-wider text-xs hover:brightness-110 active:scale-95 transition shadow-[0_4px_12px_rgba(0,255,65,0.35)]"
              >
                <div className="flex items-center space-x-1.5">
                  <Icon name="Play" size={12} fill="currentColor" />
                  <span>PLAY</span>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deck;
